import { Injectable } from "@angular/core"
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http"
import { UserInfoResponse } from "../../../core/models/UserInfoResponse"
import { environment } from "src/environments/environment"
import { Observable, of, BehaviorSubject } from "rxjs"
import { map, mergeAll, shareReplay, take, tap } from "rxjs/operators"
import { UserSuggestion } from "src/app/core/models/UserSuggestion"
import { Helpers } from "src/app/helpers"
import { UserPaginateRequest } from "../models/user/user-paginate-request"
import { toHttpParams } from "src/app/core/helpers/http-params-extensions"
import { IPostExportOnboardingDocumentsPayload } from "../../../shared/interfaces/requests"
import { IExportOnboardingDocumentsData } from "../../../shared/interfaces/responses"
import { ChangeStatusBatch } from "src/app/shared/interfaces/user"
import { ChangePassword } from "src/app/shared/interfaces/change-password"
import { HEADERS } from "./const/http-header-interception"
import { UserDocument } from "../models/user/user-documents"

const USER_INFO_LIFESPAN_MS = 1800000 // 30 minutes

@Injectable()
export class UserService {
  private userInfo$ = new BehaviorSubject<UserInfoResponse>(null)
  private getUserInfo$: Observable<UserInfoResponse>
  private _userInfoExpire: Date
  private get_user_info: string
  private get_user_suggestions: string
  private get_history_paginate: string
  private get_historic_user: string
  private get_historic_user_v1: string
  private post_export_document_onboarding: string
  private user_endpoint: string
  private changePassword_endpoint: string
  private changeLoginAccount_endpoint: string
  private userInfoLogin = new BehaviorSubject<boolean>(false)
  private getUserISOEndpoint: string
  private confirmedEmail_endpoint: string
  private checkConfirmedEmail_endpoint: string
  private get_documents_endpoint: string
  private user_find_endpoint: string
  public accountId: string

  constructor(private httpClient: HttpClient) {
    this.get_user_info =
      environment.endpointUser.url +
      environment.endpointUser.routes.get_user_info
    this.getUserISOEndpoint =
      environment.endpointUser.url +
      environment.endpointUser.routes.get_user_iso
    this.get_user_suggestions =
      environment.endpointUser.url +
      environment.endpointUser.routes.get_user_suggestions
    this.get_history_paginate =
      environment.endpointUser.url +
      environment.endpointUser.routes.get_history_paginate
    this.get_historic_user =
      environment.endpointUser.url +
      environment.endpointUser.routes.get_historic_user_by_id
    this.get_historic_user_v1 =
      environment.endpointUser.url +
      environment.endpointUser.routes.get_historic_user
    this.post_export_document_onboarding =
      environment.endpointUser.url +
      environment.endpointUser.routes.post_export_document_onboarding
    this.user_endpoint =
      environment.endpointUser.url +
      environment.endpointUser.routes.post_account
    this.changePassword_endpoint =
      environment.endpointImpersonate.url +
      environment.endpointImpersonate.routes.changePassword
    this.changeLoginAccount_endpoint =
      environment.endpointUser.url +
      environment.endpointUser.routes.put_change_login
    this.confirmedEmail_endpoint =
      environment.endpointUser.url +
      environment.endpointUser.routes.put_change_email
    this.get_documents_endpoint =
      environment.endpointUser.url +
      environment.endpointUser.routes.get_documents
    this.user_find_endpoint =
      environment.endpointUser.url +
      environment.endpointUser.routes.get_user_find
  }

  getUserInfoLogin() {
    return this.userInfoLogin.asObservable()
  }

  setUserInfoLogin(update: boolean) {
    this.userInfoLogin.next(update)
  }

  getUserISO() {
    return this.httpClient.get(this.getUserISOEndpoint)
  }

  getUserInfo(): Observable<UserInfoResponse> {
    if (!this.getUserInfo$) {
      this.setUserInfo()
    }
    if (this._userInfoExpire && new Date() < this._userInfoExpire) {
      return this.userInfo$
    }
    return this.getUserInfo$
  }

  setUserInfo() {
    this.getUserInfo$ = this.httpClient
      .get<UserInfoResponse>(this.get_user_info, {
        headers: new HttpHeaders(HEADERS),
      })
      .pipe(
        take(1),
        shareReplay(1),
        map((userInfo) => {
          this.userInfo$.next(userInfo)
          this._userInfoExpire = new Date(Date.now() + USER_INFO_LIFESPAN_MS)
          return this.userInfo$
        }),
        mergeAll()
      )
  }

  getUserSuggestions(
    term: string,
    profile: string
  ): Observable<UserSuggestion[]> {
    let params = new HttpParams().append("term", Helpers.encodeBase64Url(term))
    params = params.append("profile", profile)
    return this.httpClient.get<UserSuggestion[]>(this.get_user_suggestions, {
      params,
      headers: new HttpHeaders(HEADERS),
    })
  }

  getPaginate(userPaginateRequest: UserPaginateRequest) {
    const { ...others } = userPaginateRequest
    let params = toHttpParams(others)
    return this.httpClient.get(this.get_history_paginate, {
      params,
      headers: new HttpHeaders(HEADERS),
    })
  }

  getHistoricUser(userId: string, courseId: string) {
    return this.httpClient.get(
      this.get_historic_user.replace("{userId}", userId).replace("{courseId}", courseId),
      { headers: new HttpHeaders(HEADERS) }
    )
  }

  getHistoricUserV1(userId: string) {
    return this.httpClient.get(
      this.get_historic_user_v1.replace("{historyId}", userId),
      { headers: new HttpHeaders(HEADERS) }
    )
  }

  getFindUserStudent(fullName: string, birthDate: string) {
    return this.httpClient.get(
      this.user_find_endpoint + "?fullName="+fullName+"&birthdate="+birthDate,
      { headers: new HttpHeaders(HEADERS) }
    )
  }

  getFindUserResp(document: string) {
    return this.httpClient.get(
      this.user_find_endpoint + "?document_value="+document,
      { headers: new HttpHeaders(HEADERS) }
    )
  }

  postExportUsersOnboardingDocuments(
    data: IPostExportOnboardingDocumentsPayload
  ) {
    return this.httpClient
      .post<IExportOnboardingDocumentsData>(
        this.post_export_document_onboarding,
        data,
        { headers: new HttpHeaders(HEADERS) }
      )
      .toPromise()
  }

  putChangeStatusBatch(data: ChangeStatusBatch) {
    return this.httpClient
      .put(`${this.user_endpoint}/change-status-batch`, data, {
        headers: new HttpHeaders(HEADERS),
      })
      .toPromise()
  }

  putChangePassword(password: ChangePassword, userId: string) {
    return this.httpClient
      .put(`${this.changePassword_endpoint}${userId}`, password, {
        headers: new HttpHeaders(HEADERS),
      })
      .toPromise()
  }

  putChangeLogin(newLogin: any, userId: string) {
    return this.httpClient
      .put(`${this.changeLoginAccount_endpoint}${userId}`, newLogin, {
        headers: new HttpHeaders(HEADERS),
      })
      .toPromise()
  }

  putChangeEmail(newEmail: string, userId: string): Observable<any> {
    return this.httpClient.put(
      `${this.confirmedEmail_endpoint}/${userId}`,
      { Email: newEmail },
      {
        headers: new HttpHeaders(HEADERS),
      }
    )
  }

  getDocuments(country = 1) {
    return this.httpClient.get<Array<UserDocument>>(this.get_documents_endpoint)
  }
}
